import React, { useEffect, useMemo, useState } from 'react';
import {
  Cable,
  ChevronLeft,
  ChevronRight,
  Download,
  Gauge,
  Package,
  RotateCcw,
  Search,
  Zap,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AppBar from 'components/AppBar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import { loadMaterialInventoryRows } from '../services/materialInventoryAggregation';
import './MaterialInventory.css';
import { useAuthSession } from '../contexts/AuthSessionContext';
import { getLockedLotFilter } from '../services/authService';

const ITEMS_PER_PAGE = 10;

function getLotBadgeClass(lotName) {
  if (!lotName) return 'lot-badge-default';
  if (lotName.includes('LOT 8')) return 'lot-badge-8';
  if (lotName.includes('LOT 10')) return 'lot-badge-10';
  return 'lot-badge-default';
}

function categoryBadgeKey(category) {
  return String(category || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-');
}

function MiPageSkeleton() {
  return (
    <div className="mi-skeleton" aria-busy="true" aria-label="Loading material inventory">
      <section className="mi-intro" aria-hidden>
        <div className="mi-intro-text">
          <Skeleton className="h-7 w-56 max-w-full" />
          <Skeleton className="mt-2.5 h-4 w-96 max-w-full" />
        </div>
        <div className="mi-intro-actions">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <Skeleton className="h-9 w-[7.5rem] rounded-lg" />
        </div>
      </section>
      <div className="mi-lot-tabs" aria-hidden>
        <Skeleton className="h-9 w-28 rounded-md" />
        <Skeleton className="h-9 w-28 rounded-md" />
      </div>
      <div className="mi-summary-cards" aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="mi-summary-card mi-summary-card--skel">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <Skeleton className="mt-3 h-4 w-24" />
            <Skeleton className="mt-2 h-6 w-28" />
            <Skeleton className="mt-4 h-3 w-full" />
          </div>
        ))}
      </div>
      <div className="mi-toolbar" aria-hidden>
        <Skeleton className="h-9 w-full max-w-md rounded-lg" />
      </div>
      <div className="mi-table-shell" aria-hidden>
        <div className="mi-skel-rows">
          {Array.from({ length: 7 }, (_, r) => (
            <Skeleton key={r} className="h-10 w-full rounded-md" />
          ))}
        </div>
      </div>
    </div>
  );
}

function MaterialInventory() {
  const [searchQuery, setSearchQuery] = useState('');
  const session = useAuthSession();
  const lockedLotFilter = getLockedLotFilter(session);
  const [activeLotTab, setActiveLotTab] = useState(() => lockedLotFilter || '');
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = ITEMS_PER_PAGE;

  useEffect(() => {
    if (lockedLotFilter) setActiveLotTab(lockedLotFilter);
  }, [lockedLotFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const rows = await loadMaterialInventoryRows();
        if (!cancelled) setMaterials(rows || []);
      } catch (err) {
        console.error('Failed to load inventory from PO tracking:', err);
        if (!cancelled) {
          setMaterials([]);
          setError(err?.message || 'Failed to load material inventory.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totals = useMemo(() => {
    let poles = 0;
    let cables = 0;
    let transformers = 0;
    let meters = 0;
    materials.forEach((m) => {
      const qty = Number(m.qtyDelivered) || 0;
      const cat = String(m.category || '').toLowerCase();
      if (cat === 'poles' || cat === 'wooden poles' || cat === 'wooden pole') poles += qty;
      else if (cat === 'cables') cables += qty;
      else if (cat.includes('transformer')) transformers += qty;
      else if (cat === 'meters') meters += qty;
    });
    return { poles, cables, transformers, meters };
  }, [materials]);

  const filteredMaterials = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return materials;
    return materials.filter((material) => {
      const searchableText = [
        material.lot,
        material.materialCode,
        material.materialName,
        material.category,
        material.boqQty,
        material.qtyDelivered,
        `${material.deliveredPercent}%`,
        `${material.boqBalancePercent}%`,
      ]
        .join(' ')
        .toLowerCase();
      return searchableText.includes(query);
    });
  }, [materials, searchQuery]);

  const materialsByLot = useMemo(() => {
    return filteredMaterials.reduce((acc, material) => {
      const lotKey = material.lot || 'Unassigned';
      if (!acc[lotKey]) acc[lotKey] = [];
      acc[lotKey].push(material);
      return acc;
    }, {});
  }, [filteredMaterials]);

  const lotTabs = useMemo(() => {
    const tabs = Object.keys(materialsByLot);
    if (!lockedLotFilter) return tabs;
    return tabs.filter((lot) => lot === lockedLotFilter || String(lot).includes('LOT 10'));
  }, [materialsByLot, lockedLotFilter]);

  useEffect(() => {
    if (lotTabs.length === 0) {
      setActiveLotTab('');
      return;
    }
    if (!activeLotTab || !materialsByLot[activeLotTab]) {
      setActiveLotTab(lotTabs[0]);
    }
  }, [activeLotTab, lotTabs, materialsByLot]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeLotTab, searchQuery]);

  const activeRows = useMemo(
    () => materialsByLot[activeLotTab] || [],
    [materialsByLot, activeLotTab]
  );
  const totalFiltered = activeRows.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const indexOfFirstItem = totalFiltered > 0 ? (safePage - 1) * itemsPerPage + 1 : 0;
  const indexOfLastItem = Math.min(safePage * itemsPerPage, totalFiltered);
  const paginatedRows = useMemo(
    () => activeRows.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage),
    [activeRows, safePage, itemsPerPage]
  );

  const goToPage = (page) => {
    setCurrentPage(page);
    const tableWrapper = document.querySelector('.mi-table-wrapper');
    if (tableWrapper) {
      tableWrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const goToPreviousPage = () => {
    if (safePage > 1) goToPage(safePage - 1);
  };

  const goToNextPage = () => {
    if (safePage < totalPages) goToPage(safePage + 1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else if (safePage <= 3) {
      for (let i = 1; i <= 4; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    } else if (safePage >= totalPages - 2) {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = safePage - 1; i <= safePage + 1; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  const clearAllFilters = () => {
    setSearchQuery('');
  };

  const handleExportInventoryToExcel = () => {
    const lot8Rows = filteredMaterials
      .filter((m) => String(m.lot || '').includes('LOT 8'))
      .map((m) => ({
        'LOT Name': m.lot || '',
        'Material Code': m.materialCode || '',
        'Material Name': m.materialName || '',
        Category: m.category || '',
        'Qty Delivered': m.qtyDelivered || 0,
      }));

    const lot10Rows = filteredMaterials
      .filter((m) => String(m.lot || '').includes('LOT 10'))
      .map((m) => ({
        'LOT Name': m.lot || '',
        'Material Code': m.materialCode || '',
        'Material Name': m.materialName || '',
        Category: m.category || '',
        'Qty Delivered': m.qtyDelivered || 0,
      }));

    if (lot8Rows.length === 0 && lot10Rows.length === 0) {
      alert('No inventory data available to export.');
      return;
    }

    const workbook = XLSX.utils.book_new();
    const sheetLot8 = XLSX.utils.json_to_sheet(
      lot8Rows.length > 0 ? lot8Rows : [{ Note: 'No data for LOT 8 (Morogoro)' }]
    );
    const sheetLot10 = XLSX.utils.json_to_sheet(
      lot10Rows.length > 0 ? lot10Rows : [{ Note: 'No data for LOT 10 (Manyara)' }]
    );

    XLSX.utils.book_append_sheet(workbook, sheetLot8, 'LOT 8 Morogoro');
    XLSX.utils.book_append_sheet(workbook, sheetLot10, 'LOT 10 Manyara');

    const fileName = `Material_Inventory_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const summaryCards = [
    {
      id: 'poles',
      label: 'Wooden poles delivered',
      value: totals.poles,
      accent: '#3b82f6',
      icon: Package,
      meta: 'Wooden poles',
    },
    {
      id: 'cables',
      label: 'Cables delivered',
      value: totals.cables,
      accent: '#10b981',
      icon: Cable,
      meta: 'Cables',
    },
    {
      id: 'transformers',
      label: 'Transformers delivered',
      value: totals.transformers,
      accent: '#ef4444',
      icon: Zap,
      meta: 'Transformers',
    },
    {
      id: 'meters',
      label: 'Meters delivered',
      value: totals.meters,
      accent: '#8b5cf6',
      icon: Gauge,
      meta: 'Meters',
    },
  ];

  return (
    <div className="mi-page">
      <div className="mi-scroll">
        <div className="mi-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Sagemcom', to: '/sagemcom' },
              { label: 'Logistics', to: '/sagemcom/logistics', hideOnMobile: true },
              { label: 'Material inventory' },
            ]}
          />

          {loading && materials.length === 0 && !error ? (
            <MiPageSkeleton />
          ) : (
            <>
              <section className="mi-intro" aria-label="Material inventory overview">
                <div className="mi-intro-text">
                  <h1 className="mi-intro-title">Material inventory</h1>
                  <p className="mi-intro-subtitle">
                    Track delivered materials by LOT from purchase order deliveries
                  </p>
                </div>
                <div className="mi-intro-actions">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="mi-soft-btn h-9 w-9"
                    onClick={handleExportInventoryToExcel}
                    disabled={loading || filteredMaterials.length === 0}
                    title="Download Excel"
                    aria-label="Download Excel"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="mi-soft-btn h-9 gap-1.5"
                    onClick={clearAllFilters}
                    disabled={!searchQuery}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Clear
                  </Button>
                </div>
              </section>

              {error ? (
                <div className="mi-error" role="alert">
                  {error}
                </div>
              ) : null}

              {lotTabs.length > 0 ? (
                <div className="mi-lot-tabs" role="tablist" aria-label="Filter by LOT">
                  {(lockedLotFilter ? lotTabs.filter((lot) => lot === lockedLotFilter || String(lot).includes('LOT 10')) : lotTabs).map((lot) => (
                    <button
                      key={lot}
                      type="button"
                      role="tab"
                      aria-selected={activeLotTab === lot}
                      className={`mi-lot-tabs__btn${activeLotTab === lot ? ' is-active' : ''}`}
                      onClick={() => {
                        if (!lockedLotFilter) setActiveLotTab(lot);
                      }}
                    >
                      {lot}
                      <span className="mi-lot-tabs__count">
                        {(materialsByLot[lot] || []).length}
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}

              <div className="mi-summary-cards">
                {summaryCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <div
                      key={card.id}
                      className="mi-summary-card"
                      style={{ '--cat-accent': card.accent }}
                    >
                      <div className="mi-summary-card__top">
                        <span className="mi-summary-card__icon" aria-hidden>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="mi-summary-card__label">{card.label}</span>
                      </div>
                      <div className="mi-summary-card__count">All LOTs</div>
                      <div className="mi-summary-card__amount">
                        {card.value.toLocaleString('en-US')}
                      </div>
                      <div className="mi-summary-card__meta">
                        <div>
                          <span>Category</span>
                          <strong>{card.meta}</strong>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mi-table-section__head">
                <h2>
                  {activeLotTab
                    ? `${activeLotTab} materials`
                    : 'Material inventory'}
                  <span className="mi-table-section__count">
                    {activeRows.length}
                  </span>
                </h2>
              </div>

              <div className="mi-toolbar">
                <div className="mi-search">
                  <Search className="mi-search__icon h-4 w-4" aria-hidden />
                  <Input
                    className="mi-search__input"
                    placeholder="Search material, code, category, LOT…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search materials"
                  />
                </div>
                <div className="mi-toolbar__spacer" aria-hidden="true" />
              </div>

              <div className="mi-table-section">
                <div className="mi-table-shell">
                  <div className="mi-table-wrapper">
                    <table className="mi-table">
                      <thead>
                        <tr>
                          <th>LOT Name</th>
                          <th>Material Code</th>
                          <th>Material Name</th>
                          <th>Category</th>
                          <th>Qty Delivered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRows.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="mi-empty-row">
                              {loading
                                ? 'Loading materials…'
                                : 'No materials found for current filters.'}
                            </td>
                          </tr>
                        ) : (
                          paginatedRows.map((material) => (
                            <tr key={material.id}>
                              <td>
                                <span
                                  className={`mi-lot-badge ${getLotBadgeClass(material.lot)}`}
                                >
                                  {material.lot || '-'}
                                </span>
                              </td>
                              <td className="mi-code">{material.materialCode || '-'}</td>
                              <td className="mi-material-name">{material.materialName}</td>
                              <td>
                                <span
                                  className={`mi-category-badge category-${categoryBadgeKey(material.category)}`}
                                >
                                  {material.category}
                                </span>
                              </td>
                              <td className="mi-qty">
                                {(Number(material.qtyDelivered) || 0).toLocaleString('en-US')}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {totalFiltered > 0 ? (
                  <div className="mi-pagination">
                    <div className="mi-pagination__info">
                      Showing {indexOfFirstItem}–{indexOfLastItem} of {totalFiltered}
                    </div>
                    <div className="mi-pagination__controls">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="mi-soft-btn h-8 w-8"
                        onClick={goToPreviousPage}
                        disabled={safePage === 1}
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="mi-pagination__numbers">
                        {getPageNumbers().map((pageNum, index) =>
                          pageNum === '...' ? (
                            <span key={`ellipsis-${index}`} className="mi-pagination__ellipsis">
                              …
                            </span>
                          ) : (
                            <button
                              key={pageNum}
                              type="button"
                              className={`mi-pagination__num${safePage === pageNum ? ' is-active' : ''}`}
                              onClick={() => goToPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          )
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="mi-soft-btn h-8 w-8"
                        onClick={goToNextPage}
                        disabled={safePage === totalPages}
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MaterialInventory;
